import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Calendar, Plus, Edit, Trash2, Power } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UniversityForm } from "./UniversityForm";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatDate } from "@/utils/date";

interface University {
  id: number;
  name: string;
  campaign_start_date: string;
  campaign_end_date: string;
  is_active: boolean;
  total_tips?: number;
  total_winners?: number;
}

export function UniversityManagement() {
  const { toast } = useToast();
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: universities, refetch } = useQuery({
    queryKey: ['universities'],
    queryFn: async () => {
      console.log('Fetching universities...');
      const { data: universitiesData, error: universitiesError } = await supabase
        .from('universities')
        .select('*')
        .order('created_at', { ascending: false });

      if (universitiesError) {
        console.error('Error fetching universities:', universitiesError);
        throw universitiesError;
      }

      const tipsPromises = universitiesData.map(async (university) => {
        const { count: tipsCount } = await supabase
          .from('tips')
          .select('*', { count: 'exact', head: true })
          .eq('university_id', university.id);
        return { universityId: university.id, tipsCount };
      });

      const winnersPromises = universitiesData.map(async (university) => {
        const { count: winnersCount } = await supabase
          .from('winners')
          .select('*', { count: 'exact', head: true })
          .eq('university_id', university.id);
        return { universityId: university.id, winnersCount };
      });

      const tipsResults = await Promise.all(tipsPromises);
      const winnersResults = await Promise.all(winnersPromises);

      const enrichedUniversities = universitiesData.map(university => ({
        ...university,
        total_tips: tipsResults.find(t => t.universityId === university.id)?.tipsCount || 0,
        total_winners: winnersResults.find(w => w.universityId === university.id)?.winnersCount || 0
      }));

      console.log('Universities fetched:', enrichedUniversities);
      return enrichedUniversities as University[];
    },
  });

  const handleDelete = async (id: number) => {
    try {
      console.log('Deleting university:', id);
      const { error } = await supabase
        .from('universities')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "University deleted successfully",
      });
      refetch();
    } catch (error) {
      console.error('Error deleting university:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete university",
      });
    }
  };

  const handleSubmit = async (data: Omit<University, 'id' | 'is_active'>) => {
    try {
      console.log('Submitting university data:', data);
      
      if (selectedUniversity) {
        const { error: updateError } = await supabase
          .from('universities')
          .update({
            name: data.name,
            campaign_start_date: data.campaign_start_date,
            campaign_end_date: data.campaign_end_date
          })
          .eq('id', selectedUniversity.id);

        if (updateError) throw updateError;
        
        toast({
          title: "Success",
          description: "University updated successfully",
        });
      } else {
        const { error: insertError } = await supabase
          .from('universities')
          .insert([{
            name: data.name,
            campaign_start_date: data.campaign_start_date,
            campaign_end_date: data.campaign_end_date,
            is_active: false
          }]);

        if (insertError) throw insertError;
        
        toast({
          title: "Success",
          description: "University added successfully",
        });
      }

      setIsDialogOpen(false);
      setSelectedUniversity(null);
      refetch();
    } catch (error) {
      console.error('Error submitting university:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save university",
      });
    }
  };

  const handleToggleActive = async (university: University) => {
    try {
      console.log('Toggling active status for university:', university.id);
      const { error } = await supabase
        .from('universities')
        .update({ is_active: !university.is_active })
        .eq('id', university.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `${university.name} is now ${!university.is_active ? 'active' : 'inactive'}`,
      });
      refetch();
    } catch (error) {
      console.error('Error toggling university active status:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update university status",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Universities</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => setSelectedUniversity(null)}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add University
            </Button>
          </DialogTrigger>
          <UniversityForm
            university={selectedUniversity}
            onSubmit={handleSubmit}
            onClose={() => {
              setIsDialogOpen(false);
              setSelectedUniversity(null);
            }}
          />
        </Dialog>
      </div>

      <div className="bg-white rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Campaign Start</TableHead>
              <TableHead>Campaign End</TableHead>
              <TableHead>Total Tips</TableHead>
              <TableHead>Total Winners</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {universities?.map((university) => (
              <TableRow key={university.id}>
                <TableCell>{university.name}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {formatDate(university.campaign_start_date)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {formatDate(university.campaign_end_date)}
                  </div>
                </TableCell>
                <TableCell>{university.total_tips}</TableCell>
                <TableCell>{university.total_winners}</TableCell>
                <TableCell>
                  <Button
                    variant={university.is_active ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleToggleActive(university)}
                    className="gap-2"
                  >
                    <Power className={`h-4 w-4 ${university.is_active ? 'text-green-500' : 'text-gray-500'}`} />
                    {university.is_active ? 'Active' : 'Inactive'}
                  </Button>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedUniversity(university);
                        setIsDialogOpen(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(university.id)}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
